import type { CSSProperties } from 'react';

import { backgroundColors } from '../assets/colors';
import { computeColor } from './colors';
import { buildPatternBackgroundLayer } from './patterns/buildPatternBackgroundLayer';
import type { PatternConfig } from './patterns/types';

export const TRANSPARENT_COLOR = 'transparent';

/**
 *
 * Get background style from background colors.
 *
 * Returned empty object if the parameter is `undefined`. Otherwise,
 * will return an object with a `background` property.
 *
 * @param background
 */
export const getBackgroundColorStyle = (background?: string): Pick<CSSProperties, 'background'> => {
  if (!background) return {};

  return {
    background: computeColor(background, backgroundColors),
  };
};

/**
 *
 * Get the composed background style for an element that may carry an SVG pattern overlay.
 *
 * When no pattern is set this returns exactly what `getBackgroundColorStyle` returns, so the
 * output is byte-identical to the pre-pattern behavior. When a pattern is set, its repeating
 * SVG layer is composited ON TOP of the base color/gradient (earlier layer paints on top), with
 * the base color/gradient as the final layer so it shows through the pattern's transparent areas.
 *
 * @param backgroundColor base color or gradient string
 * @param backgroundPattern optional pattern overlay config
 */
export const getBackgroundLayerStyle = (
  backgroundColor?: string,
  backgroundPattern?: PatternConfig | null,
): Pick<CSSProperties, 'background'> => {
  const baseStyle = getBackgroundColorStyle(backgroundColor);
  const layer = buildPatternBackgroundLayer(backgroundPattern);

  if (!layer) return baseStyle;

  const base = baseStyle.background;
  return { background: base ? `${layer}, ${base}` : layer };
};

/**
 *
 * Get background computed color
 *
 * Returned computed color.
 *
 * @param background
 */
export const getBackgroundColor = (background?: string): string | undefined => {
  if (!background) return '';
  // computeColor rejects 'transparent', which would let the navbar inherit the white page background.
  if (background === TRANSPARENT_COLOR) return background;

  return computeColor(background, backgroundColors);
};

/**
 *
 * Get background darken style
 *
 * Returned object with `background` and `filter` properties.
 *
 * @param backgroundDarken
 */
export const getBackgroundDarkenStyle = (
  backgroundDarken?: number | boolean,
): Pick<CSSProperties, 'background' | 'filter'> => {
  const darkenValue = typeof backgroundDarken === 'boolean' ? (backgroundDarken ? 0.4 : 0) : backgroundDarken || 0;

  return {
    filter: `brightness(${1 - darkenValue})`,
  };
};
